import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getEquipment, equipmentBase, type ResolvedEquipment } from '../lib/equipment';
import { SITE_URL, getStrings, altLinks, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { Thumb } from '../components/Thumb';

type Category = ResolvedEquipment['category'];

export default function EquipmentIndex({ lang = 'en' as Lang }: { lang?: Lang }) {
  const c = getStrings(lang).equipmentChrome;
  const base = equipmentBase(lang);
  const all = getEquipment(lang);
  const [params] = useSearchParams();

  const [category, setCategory] = useState<Category | 'all'>('all');
  const [homeOnly, setHomeOnly] = useState(params.get('home') === '1');

  const filtered = all.filter((e) => {
    if (category !== 'all' && e.category !== category) return false;
    if (homeOnly && !e.homeFriendly) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={c.indexMetaTitle}
        description={c.indexMetaDescription}
        canonical={`${SITE_URL}${base}`}
        lang={lang}
        alternates={altLinks('/armwrestling/equipment')}
      />
      <BlogHeader lang={lang} />

      <main className="container mx-auto px-6 py-16 max-w-5xl">
        <h1 className="text-4xl md:text-5xl font-extrabold text-ink mb-4">{c.indexHeading}</h1>
        <p className="text-muted text-lg mb-10 max-w-2xl">{c.indexTagline}</p>

        <div className="flex flex-wrap gap-4 mb-10">
          <FilterGroup
            label={c.filterCategoryLabel}
            value={category}
            onChange={(v) => setCategory(v as Category | 'all')}
            options={[
              { value: 'all', label: c.filterAll },
              { value: 'table', label: c.category.table },
              { value: 'grip', label: c.category.grip },
              { value: 'wrist', label: c.category.wrist },
              { value: 'strap', label: c.category.strap },
              { value: 'accessory', label: c.category.accessory },
            ]}
          />
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={homeOnly}
              onChange={(e) => setHomeOnly(e.target.checked)}
              className="w-4 h-4 rounded border-line bg-surface accent-primary"
            />
            <span className="text-faint font-medium">{c.homeOnlyLabel}</span>
          </label>
        </div>

        {filtered.length === 0 ? (
          <p className="text-faint">{c.noResults}</p>
        ) : (
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((e) => (
              <li key={e.slug}>
                <Link
                  to={`${base}/${e.slug}`}
                  className="group block h-full overflow-hidden rounded-2xl border border-line bg-surface/60 hover:border-primary/40 transition"
                >
                  <div className="relative">
                    <Thumb
                      src={e.imageUrl}
                      alt={e.t.name}
                      emoji={e.emoji}
                      contain
                      className="aspect-[4/3] w-full"
                    />
                    <span className="absolute top-2 right-2 rounded-full bg-surface/90 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-ink uppercase tracking-wide shadow-sm">
                      {c.category[e.category]}
                    </span>
                  </div>
                  <div className="p-4">
                    <h2 className="text-ink font-bold group-hover:text-primary transition mb-1">{e.t.name}</h2>
                    <p className="text-muted text-sm line-clamp-2">{e.t.shortDescription}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>

      <Footer lang={lang} />
    </div>
  );
}

function FilterGroup<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-faint font-medium">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="bg-surface border border-line text-ink rounded-full px-3 py-1.5 text-sm focus:outline-none focus:border-primary/50"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
